<template>
	<div class="apparatus-detail-container">
		<DcHeader title="医械详情" :back-method="onBack" />

		<div class="content-wrapper">
			<div class="info-list">
				<div class="info-item" v-for="(item, i) in productList" :key="i">
					<div class="info-label">{{ item.label }}</div>
					<component :is="i === 0 ? 'h1' : 'div'" class="info-value">{{ item.value }}</component>
				</div>
			</div>

			<!-- 同企业产品 -->
			<div class="related-section same-company">
				<div class="section-header">同企业产品展示</div>
				<div v-if="similarityList.length" class="item-list">
					<NuxtLink v-for="item in similarityList" :key="item.id" :to="`/apparatus/${item.id}`" class="related-item">
						<div class="item-content">
							<div class="item-title">{{ item.cpmctymc }} {{ item.ggxh }}</div>
							<div class="item-code">{{ item.zczbhhzbapzbh }}</div>
						</div>
						<van-icon name="arrow" color="#909399" size="16" />
					</NuxtLink>
				</div>
				<div v-else class="empty-state">
					<div class="empty-state__image-wrap">
						<img src="@/assets/images/empty.png" alt="暂无相关数据" class="empty-state__image" />
					</div>
					<div class="empty-state__text">抱歉，暂无相关数据</div>
				</div>
			</div>

			<!-- 更多同类器械 -->
			<div class="related-section same-type">
				<div class="section-header">更多同类器械</div>
				<div v-if="goodsList.length" class="item-list">
					<NuxtLink v-for="item in goodsList" :key="item.id" :to="`/apparatus/${item.id}`" class="related-item">
						<div class="item-content">
							<div class="item-title">{{ item.cpmctymc }} {{ item.ggxh }}</div>
							<div class="item-code">{{ item.zczbhhzbapzbh }}</div>
						</div>
						<van-icon name="arrow" color="#ccc" size="16" />
					</NuxtLink>
				</div>
				<div v-else class="empty-state">
					<div class="empty-state__image-wrap">
						<img src="@/assets/images/empty.png" alt="暂无相关数据" class="empty-state__image" />
					</div>
					<div class="empty-state__text">抱歉，暂无相关数据</div>
				</div>
			</div>
			<div class="no-more-data">没有更多数据了</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useSeoMeta } from '#imports';
import { productInfoList } from './config/index.js';
import { getMdm } from '@/api/apparatus.js';
import DcHeader from '@/components/DcHeader.vue';

const route = useRoute();
const router = useRouter();

// 支持同时兼容 /apparatus/:id 和 /apparatus/:id.html
const apparatusId = computed(() => String(route.params.id ?? '').replace(/\.html$/, ''));

// canonical：强制使用 /apparatus/:id.html 格式
const config = useRuntimeConfig();
const pcBase = (config.public.publicPcUrl || '').replace(/\/$/, '');
useHead(() => ({
	meta: [
		{ name: 'applicable-device', content: 'mobile' },
		{ 'http-equiv': 'Cache-Control', content: 'no-siteapp' },
		{ 'http-equiv': 'Cache-Control', content: 'no-transform' },
	],
	link: [
		{
			rel: 'canonical',
			href: pcBase ? `${pcBase}/apparatus/${apparatusId.value}.html` : `/apparatus/${apparatusId.value}.html`,
		},
	],
}));

// Handle back button: go back if has history, else go to list page
const onBack = () => {
	if (document.referrer) {
		navigation.back();
	} else {
		void navigateTo('/apparatus');
	}
};

// Default SEO values
const DEFAULT_TITLE = '医疗器械详情-国械在线';
const DEFAULT_DESC = '医疗器械详细信息';
const DEFAULT_KEYWORDS = '医疗器械';

// Fetch data
const { data: mdmRes, error } = await useAsyncData(`apparatus-detail-${apparatusId.value}`, () => getMdm({ id: apparatusId.value }));
if (error.value) {
	console.error('Fetch Error:', error.value);
}

// Process response
const productInfo = computed(() => {
	if (mdmRes.value?.code == 200) {
		return mdmRes.value.data?.[0] || {};
	}
	return {};
});

// Handle rate limit
if (mdmRes.value?.code === 'MDMBLOCK519' && import.meta.client) {
	const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
	router.push(`/verification?returnUrl=${returnUrl}`);
}

// 同企业产品列表
const similarityList = computed(() => {
	return productInfo.value?.similarityMdm || [];
});

// 更多同类器械列表
const goodsList = computed(() => {
	return productInfo.value?.goodsMdm || [];
});
// Extract fields
const cpmctymc = computed(() => productInfo.value.cpmctymc || '');
const ggxh = computed(() => productInfo.value.ggxh || '');
const zczbhhzbapzbh = computed(() => productInfo.value.zczbhhzbapzbh || '');
const ylqxzcrbarmc = computed(() => productInfo.value.ylqxzcrbarmc || '');

// Title: {产品名称/通用名称} {型号规格} {注册证编号或者备案凭证编号}-国械在线
const title = computed(() => {
	if (!cpmctymc.value && !ggxh.value && !zczbhhzbapzbh.value) {
		return DEFAULT_TITLE;
	}
	return `${cpmctymc.value} ${ggxh.value} ${zczbhhzbapzbh.value}-国械在线`.trim();
});

// Description
const description = computed(() => {
	if (!cpmctymc.value) return DEFAULT_DESC;
	return `${cpmctymc.value} ${ggxh.value}相关UDI数据由国械在线提供。${cpmctymc.value} ${ggxh.value}详细UDI信息如下：注册证/备案凭证编号为${zczbhhzbapzbh.value}、规格型号为${ggxh.value}、注册人/备案人为${ylqxzcrbarmc.value}。`;
});

// Keywords
const keywords = computed(() => {
	const arr = [cpmctymc.value, ggxh.value, zczbhhzbapzbh.value, ylqxzcrbarmc.value].filter(Boolean);
	return arr.length ? arr.join(',') : DEFAULT_KEYWORDS;
});

// SEO Meta
useSeoMeta({
	title: () => title.value,
	ogTitle: () => title.value,
	description: () => description.value,
	ogDescription: () => description.value,
	keywords: () => keywords.value,
	ogImage: 'https://uat-cmdc.oss-cn-beijing.aliyuncs.com/productFile/2022/08/18/9af2756f-27bf-4d1e-b0d1-8397e82bb2a0.png',
});

// Processed product list
const productList = computed(() => {
	return productInfoList.map(item => {
		let value = productInfo.value[item.prop];
		if (item.enum && value !== undefined) {
			const enumItem = item.enum.find(e => e.value == value);
			if (enumItem) {
				value = enumItem.label;
			}
		}
		return {
			...item,
			value: value || '',
		};
	});
});
</script>

<style lang="scss" scoped>
.apparatus-detail-container {
	min-height: 100vh;
	background: #f5f5f5;
}

.content-wrapper {
	padding-top: 8px;
}

.product-header {
	background: #fff;
	padding: 1rem;
	margin-bottom: 0.75rem;

	.title {
		font-size: 1.17rem;
		font-weight: 500;
		color: #1f1f1f;
		line-height: 1.5;
		word-break: break-all;
	}
}

.info-list {
	background: #fff;
	overflow: hidden;
}

.info-item {
	display: flex;
	align-items: flex-start;
	padding: 12px;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}

	.info-label {
		width: 120px;
		max-width: 120px;
		flex-shrink: 0;
		font-size: 11px;
		color: #595959;
		line-height: 1.5;
	}

	.info-value {
		margin-left: auto;
		font-size: 11px;
		color: #333333;
		line-height: 1.5;
		word-break: break-all;
		text-align: left;
	}
}

// 相关产品模块
.related-section {
	margin-top: 8px;
	padding-bottom: 0px;
	position: relative;
	overflow: hidden;
	background: linear-gradient(223deg, #ccdfff 0%, #fcfeff 100%);

	&::before {
		content: '';
		position: absolute;
		top: 8px;
		right: 4px;
		width: 103px;
		height: 64px;
		background-repeat: no-repeat;
		background-position: top right;
		background-size: cover;
		opacity: 1;
		pointer-events: none;
		z-index: 0;
	}

	> * {
		position: relative;
		z-index: 1;
	}
}

.same-company::before {
	background-image: url('@/assets/images/apparatus/same_company.png');
}

.same-type::before {
	background-image: url('@/assets/images/apparatus/same_type.png');
}

.section-header {
	background: transparent;
	padding: 14px 0;
	text-align: center;
	font-size: 16px;
	font-weight: 600;
	color: #333333;
}

.item-list {
	margin: 0 8px 16px;
	background: rgba(255, 255, 255, 0.85);
	border-radius: 5px;
	overflow: hidden;
}

.empty-state {
	margin: 0 8px 16px;
	padding: 24px 16px 24px;
	background: rgba(255, 255, 255, 0.85);
	border-radius: 5px;
	text-align: center;
}

.empty-state__image-wrap {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px 8px;
	padding-bottom: 0;
	background: #fff;
}

.empty-state__image {
	display: block;
	width: 207px;
	height: 162px;
	object-fit: contain;
	margin: 0 auto;
	flex: 0 0 auto;
	border: 1px dashed #d9d9d9;
	border-radius: 4px;
	background: #fff;
}

.empty-state__text {
	margin-top: 8px;
	font-size: 12px;
	color: #999;
}

.no-more-data {
	padding: 9px 0;
	background: #f3f6fb;
	text-align: center;
	font-size: 12px;
	line-height: 1.5;
	margin-bottom: 8px;
	color: #999999;
}

.related-item {
	display: flex;
	align-items: center;
	padding: 12px 12px;
	border-bottom: 1px solid #f0f0f0;
	text-decoration: none;
	color: inherit;

	&:last-child {
		border-bottom: none;
	}

	&:active {
		background: #f5f5f5;
	}

	.item-content {
		flex: 1;
		min-width: 0;
	}

	.item-title {
		font-size: 14px;
		font-weight: 500;
		color: #1f1f1f;
		line-height: 1.5;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.item-code {
		font-size: 12px;
		color: #999;
		line-height: 1.4;
		margin-top: 2px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.van-icon {
		margin-left: 8px;
		flex-shrink: 0;
	}
}
</style>
